import {
  FieldName,
  SecurityConfigurationCard,
  ValueAndAction,
  ValueAndActionWrapper,
} from "features/settings/common/styles";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import RouterLink from "common/Buttons/RouterLink";
import {
  CHANGE_NAME_SETTINGS_PATH,
  CHANGE_PASSWORD_SETTINGS_PATH,
  CONNECT_EMAIL_SETTINGS_PATH,
  IS_WHOP,
} from "common/constants";
import { useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service";
import { CHANGE_FULL_NAME_FEATURE_FLAG } from "@shared/common/constants.ts";
import StatusTag from "common/Tags/StatusTag.tsx";
import { useTheme } from "styled-components";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { Pill } from "common/NewLook";
import Panel from "common/NewLook/Panel.tsx";
import SettingsRowLabel from "features/settings/NewLook/SettingsRowLabel.tsx";

interface BasicInfoProps {
  /** The new look shows the password row in the security card instead. */
  hidePassword?: boolean;
}

export const PasswordRow = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const { isNewLook } = useTheme();
  return (
    <ValueAndActionWrapper>
      <FieldName>
        {isNewLook ? (
          <SettingsRowLabel>{t("basic_info.password")}</SettingsRowLabel>
        ) : (
          t("basic_info.password")
        )}
      </FieldName>
      <ValueAndAction>
        <Text>{isNewLook ? "••••••••" : "*********"}</Text>
        <RouterLink to={CHANGE_PASSWORD_SETTINGS_PATH}>
          {t("basic_info.change")}
        </RouterLink>
      </ValueAndAction>
    </ValueAndActionWrapper>
  );
};

const BasicInfo = ({ hidePassword }: BasicInfoProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const theme = useTheme();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const hasChangeNameFeatureFlag = user?.hasFeatureFlag(
    CHANGE_FULL_NAME_FEATURE_FLAG
  );
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const getEmail = () => {
    if (!user?.email && theme.isNewLook) {
      return <Pill>{t("basic_info.not_connected")}</Pill>;
    }
    return user?.email ? (
      <Text>{user?.email}</Text>
    ) : (
      <StatusTag
        color={theme.colors.red}
        text={t("basic_info.not_connected")}
        style={isBottomNavigator ? { padding: "2px 8px" } : undefined}
      />
    );
  };

  const rows = (
    <>
      <ValueAndActionWrapper>
        <FieldName>{t("basic_info.full_name")}</FieldName>
        <ValueAndAction>
          <Text>{user?.full_name}</Text>
          {hasChangeNameFeatureFlag && (
            <RouterLink to={CHANGE_NAME_SETTINGS_PATH}>
              {t("basic_info.change")}
            </RouterLink>
          )}
        </ValueAndAction>
      </ValueAndActionWrapper>
      {!IS_WHOP && (
        <ValueAndActionWrapper>
          <FieldName>{t("basic_info.user_name")}</FieldName>
          <ValueAndAction>
            <Text>{user?.username}</Text>
          </ValueAndAction>
        </ValueAndActionWrapper>
      )}
      {!IS_WHOP && (
        <>
          <ValueAndActionWrapper>
            <FieldName>{t("basic_info.email")}</FieldName>
            <ValueAndAction>
              {getEmail()}
              {!user?.email && (
                <RouterLink to={CONNECT_EMAIL_SETTINGS_PATH}>
                  {t("basic_info.connect")}
                </RouterLink>
              )}
            </ValueAndAction>
          </ValueAndActionWrapper>
          <ValueAndActionWrapper>
            <FieldName>UID</FieldName>
            <ValueAndAction>
              <Text>{user?.uid?.toString()}</Text>
            </ValueAndAction>
          </ValueAndActionWrapper>
          {!hidePassword && <PasswordRow />}
        </>
      )}
    </>
  );

  if (theme.isNewLook) {
    return (
      <Panel title={t("basic_info.title")} flush>
        {rows}
      </Panel>
    );
  }

  return (
    <SecurityConfigurationCard>
      <Title level={3}>{t("basic_info.title")}</Title>
      {rows}
    </SecurityConfigurationCard>
  );
};

export default BasicInfo;
